import styled, { css } from 'styled-components'
import {
  textStyleSizeOptions,
  textStyleWeightOptions,
  textStyleSizeStyleOptions,
  textStyleWeightStyleOptions
} from '../../fundamentals/text-styles/constants'
import colorsOptions, {
  applyOpacity
} from '../../fundamentals/colors-styles/constants'
import {
  contextMenuAlignOptions,
  contextMenuArrowAlignmentOptions,
  contextMenuPadding,
  contextMenuSideOptions,
  contextMenuSize
} from './constants'
import { motion } from 'framer-motion'

export const TextContainer = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 2;

  /* a flex item won't shrink past its content unless min-width is cleared,
     which is what lets the title below actually reach the ellipsis */
  ${({ truncate }) =>
    truncate &&
    css`
      min-width: 0;
    `}
`

export const Title = styled.span`
  display: flex;
  flex-direction: row;

  ${({ size, weight }) => css`
    ${textStyleWeightStyleOptions[weight]}
    ${textStyleSizeStyleOptions[size]}
  `};

  /* block, not flex: text-overflow has no effect on the anonymous flex item a
     flex container wraps its bare text in */
  ${({ truncate }) =>
    truncate &&
    css`
      display: block;
      max-width: 100%;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    `}

  ${({ isItemSelected }) =>
    isItemSelected &&
    css`
      ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-700']]};
    `}

  color:  ${({ color }) => color ?? colorsOptions.greys['grey-100']}};
`

export const SecondaryText = styled.span`
  display: flex;
  flex-direction: row;
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-500']]};
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-S']]};
  color: ${colorsOptions.greys['grey-70']};
`
export const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${({ truncate }) =>
    truncate &&
    css`
      flex: 1;
      min-width: 0;
    `}
`

export const ItemContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  cursor: pointer;

  ${({ padding }) => css`
    padding: ${contextMenuPadding[padding]};
  `}

  ${({ size }) => css`
    min-height: ${contextMenuSize[size]};
  `}

  // state
  background-color: ${colorsOptions.greys.white};

  &:hover {
    background-color: ${colorsOptions.greys['grey-05']};
  }

  &:active {
    background-color: ${colorsOptions.greys['grey-10']};
  }

  &[data-selected='true'] {
    background-color: ${colorsOptions.greys['grey-20']};

    &:hover {
      background-color: ${colorsOptions.greys['grey-30']};
    }

    &:active {
      background-color: ${colorsOptions.greys['grey-40']};
    }
  }

  &[data-disabled='true'] {
    cursor: not-allowed;
    pointer-events: none;
    background-color: ${colorsOptions.greys.white};
    ${Title} {
      color: ${colorsOptions.greys['grey-40']};
    }
  }
`
export const ItemContainerDestructive = styled(ItemContainer)`
  background-color: ${colorsOptions.greys.white};
  ${Title} {
    color: ${colorsOptions.semantics.danger['error-50']};
  }
  svg,
  path {
    stroke: ${colorsOptions.semantics.danger['error-50']};
  }

  &:hover {
    background-color: ${colorsOptions.semantics.danger['error-10']};
  }

  &:active {
    background-color: ${colorsOptions.semantics.danger['error-20']};
  }

  &[data-selected='true'] {
    background-color: ${colorsOptions.semantics.danger['error-20']};

    &:hover {
      background-color: ${colorsOptions.semantics.danger['error-30']};
    }

    &:active {
      background-color: ${colorsOptions.semantics.danger['error-40']};
    }
  }
`

export const ItemContainerRestricted = styled(ItemContainer)`
  background-color: ${colorsOptions.greys.white};
  ${Title} {
    color: ${colorsOptions.greys['grey-70']};
  }
  pointer-events: none;
  cursor: not-allowed;

  &:hover {
    background-color: ${colorsOptions.greys['grey-05']};
  }

  &:active {
    background-color: ${colorsOptions.greys['grey-10']};
  }

  &[data-selected='true'] {
    background-color: ${colorsOptions.greys.white};

    &:hover {
      background-color: ${colorsOptions.greys['grey-05']};
    }

    &:active {
      background-color: ${colorsOptions.greys['grey-10']};
    }
  }
`

// Divider

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colorsOptions.greys['grey-20']};
`

// Content

export const RootContainer = styled.div`
  position: relative;
  display: block;
  width: fit-content;
`

// List
export const List = styled.div`
  color: ${colorsOptions.greys.white};
  border-radius: 1px;
  box-shadow: 0px 8px 24px 0px rgba(120, 120, 135, 0.2);
  position: absolute;
  z-index: 99;
  overflow: visible;
  border-radius: 8px;
  background-color: ${colorsOptions.greys.white};

  & > div:first-of-type {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  & > div:last-of-type {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  width: ${({ width }) => width ?? '100%'};

  ${({
    side = contextMenuSideOptions.BOTTOM,
    align = contextMenuAlignOptions.CENTER,
    sideXOffset = 0
  }) => {
    let marginValue = '0'
    if (
      side === contextMenuSideOptions.BOTTOM ||
      side === contextMenuSideOptions.TOP
    ) {
      if (align === contextMenuAlignOptions.START) {
        marginValue = `margin-right: ${sideXOffset}px;`
      } else if (align === contextMenuAlignOptions.END) {
        marginValue = `margin-left: ${sideXOffset}px;`
      }
    } else if (
      side === contextMenuSideOptions.LEFT ||
      side === contextMenuSideOptions.RIGHT
    ) {
      if (align === contextMenuAlignOptions.START) {
        marginValue = `margin-bottom: ${sideXOffset}px;`
      } else if (align === contextMenuAlignOptions.END) {
        marginValue = `margin-top: ${sideXOffset}px;`
      }
    }

    return css`
      ${marginValue}
    `
  }};

  ${({ side = contextMenuSideOptions.BOTTOM, sideYOffSet = 0 }) => {
    switch (side) {
      case 'top':
        return css`
          bottom: calc(100% + ${sideYOffSet}px);
          left: 50%;
          transform: translateX(-50%);
        `
      case 'right':
        return css`
          top: 50%;
          left: calc(100% + ${sideYOffSet}px);
          transform: translateY(-50%);
        `
      case 'bottom':
        return css`
          top: calc(100% + ${sideYOffSet}px);
          left: 50%;
          transform: translateX(-50%);
        `
      case 'left':
        return css`
          top: 50%;
          right: calc(100% + ${sideYOffSet}px);
          transform: translateY(-50%);
        `
      default:
        return null
    }
  }}

  ${({
    align = contextMenuAlignOptions.CENTER,
    side = contextMenuSideOptions.BOTTOM
  }) => {
    if (
      (side === contextMenuSideOptions.TOP ||
        side === contextMenuSideOptions.BOTTOM) &&
      (align === contextMenuAlignOptions.START ||
        align === contextMenuAlignOptions.END)
    ) {
      return css`
        right: ${align === contextMenuAlignOptions.START ? '0' : 'auto'};
        left: ${align === contextMenuAlignOptions.END ? '0' : 'auto'};
        transform: none;
      `
    }
    if (
      (side === contextMenuSideOptions.LEFT ||
        side === contextMenuSideOptions.RIGHT) &&
      (align === contextMenuAlignOptions.START ||
        align === contextMenuAlignOptions.END)
    ) {
      return css`
        bottom: ${align === contextMenuAlignOptions.START ? '0' : 'auto'};
        top: ${align === contextMenuAlignOptions.END ? '0' : 'auto'};
        transform: none;
      `
    }
    return null
  }}
`

export const ContentContainer = styled.div`
  width: 100%;
  overflow-y: auto;
  height: ${({ height }) => height ?? 'auto'};
  max-height: ${({ maxHeight }) => maxHeight ?? 'fit-content'};

  /* This used to set display:initial on ::-webkit-scrollbar, which resolves to
     inline and left the gutter reserved but the handle unpainted — an empty
     strip down the right side. Styling the parts gives it back. */
  ${({ hideScrollbar }) =>
    hideScrollbar
      ? css`
          scrollbar-width: none;

          &::-webkit-scrollbar {
            display: none;
          }
        `
      : css`
          scrollbar-width: thin;
          scrollbar-color: ${colorsOptions.greys['grey-30']} transparent;

          &::-webkit-scrollbar {
            width: 8px;
          }

          &::-webkit-scrollbar-track {
            background: transparent;
          }

          &::-webkit-scrollbar-thumb {
            /* padding around the handle, drawn through the transparent border */
            background-color: ${colorsOptions.greys['grey-30']};
            background-clip: content-box;
            border: 2px solid transparent;
            border-radius: 4px;
          }

          &::-webkit-scrollbar-thumb:hover {
            background-color: ${colorsOptions.greys['grey-40']};
          }
        `}
`

// Arrow
export const Arrow = styled.span`
  width: 0;
  height: 0;
  border-style: solid;
  position: absolute;
  border-width: 5px;
  border-style: solid;

  ${({ side, arrowAlignment }) => {
    let leftValue
    let marginLeftValue
    let topValue
    let marginTopValue

    if (arrowAlignment === contextMenuArrowAlignmentOptions.START) {
      leftValue = '0%'
      marginLeftValue = '5px'
      topValue = '0%'
      marginTopValue = '5px'
    } else if (arrowAlignment === contextMenuArrowAlignmentOptions.CENTER) {
      leftValue = '50%'
      marginLeftValue = '-5px'
      topValue = '50%'
      marginTopValue = '-5px'
    } else if (arrowAlignment === contextMenuArrowAlignmentOptions.END) {
      leftValue = '100%'
      marginLeftValue = '-15px'
      topValue = '100%'
      marginTopValue = '-15px'
    } else {
      leftValue = '50%'
      marginLeftValue = '-5px'
      topValue = '50%'
      marginTopValue = '-5px'
    }

    switch (side) {
      case 'top':
        return css`
          top: 100%;
          border-color: white transparent transparent transparent;
          left: ${leftValue};
          margin-left: ${marginLeftValue};
          transform: translateY(-1px);
        `
      case 'right':
        return css`
          right: 100%;
          border-color: transparent white transparent transparent;
          top: ${topValue};
          margin-top: ${marginTopValue};
        `
      case 'bottom':
        return css`
          bottom: 100%;
          border-color: transparent transparent white transparent;
          left: ${leftValue};
          margin-left: ${marginLeftValue};
        `
      case 'left':
        return css`
          left: 100%;
          border-color: transparent transparent transparent white;
          top: ${topValue};
          margin-top: ${marginTopValue};
        `
      default:
        return null
    }
  }}
`
// trigger

export const TriggerContainer = styled.div`
  position: relative;
  cursor: default;
`

// isLoading
export const Loading = styled(motion.div)`
  width: 182px;
  height: 20px;
  background-color: ${colorsOptions.greys['grey-20']};
  border-radius: 15px;
  margin: 8px;
`

// Background overlay

export const ContextMenuOverlay = styled.div`
  display: ${({ activate = false }) => (activate ? 'block' : 'none')};
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${applyOpacity(colorsOptions.greys['grey-100'], 0.25)};
  z-index: 98;
`
